import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "../../../lib/ui";
import { useLatest } from "../../../hooks/useLatest";
import type { AppNotification } from "../../../components/notifications/NotificationCenter";

type UseNotificationsOptions = {
  socket: any;
  token: string;
  friendsOpen: boolean;
  onOpenSettings: (section?: string) => void;
  handleOpenProfile: (userId: string) => void;
  refreshFriends: () => void;
};

/**
 * Notification-center state (persisted list + open flag + unread count +
 * per-item action wiring), the friend-request badge count, and the
 * join-request/user-update/friends-update socket handlers that feed them
 * (step 3.3).
 *
 * The socket effect re-registers on token change because the handlers reach
 * the API through closures over `token` (getUserSummary /
 * fetchIncomingFriendRequests) — they don't close over the current
 * conversation, so unlike the shared effect they were lifted from there is no
 * space/channel dep. `onJoinRequestsChanged` stayed behind in ChatLayout: it
 * only refreshes the space list and touches no notification state.
 *
 * `refreshFriends` stays parent-side (it also feeds friendsList, step 4.1);
 * it reports the incoming-request count through the returned
 * setFriendReqCount, and onFriendsUpdate's fallback path calls it via the
 * input callback. `notifySeenIdsRef` is the in-page notification dedup set
 * ChatLayout passes to handleNotification from its message handlers.
 */
export function useNotifications({
  socket,
  token,
  friendsOpen,
  onOpenSettings,
  handleOpenProfile,
  refreshFriends,
}: UseNotificationsOptions) {
  const NOTIF_KEY = '__echo_notifications__';
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<AppNotification[]>(() => {
    try {
      const raw = localStorage.getItem(NOTIF_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(NOTIF_KEY, JSON.stringify(notifications)); } catch {}
  }, [notifications]);
  const unreadNotificationCount = useMemo(() => notifications.filter(n => n.unread).length, [notifications]);

  function addNotification(n: AppNotification) {
    setNotifications((prev) => {
      if (prev.some(p => p.id === n.id)) return prev;
      return [{ ...n, unread: true }, ...prev].slice(0, 200);
    });
  }
  function removeNotification(id: string) {
    setNotifications((prev) => prev.filter(n => n.id !== id));
  }
  function markAllRead() {
    setNotifications((prev) => prev.map(n => ({ ...n, unread: false })));
  }
  const userSummaryCacheRef = useRef<Map<string, { id: string; username?: string; name?: string; avatarUrl?: string | null }>>(new Map());
  async function getUserSummary(uid: string) {
    const key = String(uid || '');
    if (!key) return null;
    const cached = userSummaryCacheRef.current.get(key);
    if (cached) return cached;
    try {
      const res = await api.getAuth(`/users/profile?userId=${encodeURIComponent(key)}`, token);
      const summary = { id: res?.id || key, username: res?.username, name: res?.name, avatarUrl: res?.avatarUrl ?? null };
      userSummaryCacheRef.current.set(key, summary);
      return summary;
    } catch {
      return null;
    }
  }
  async function fetchIncomingFriendRequests() {
    try {
      const res = await api.getAuth('/friends/requests', token);
      const incoming = Array.isArray(res?.incoming) ? res.incoming : [];
      return incoming;
    } catch {
      return [];
    }
  }
  async function addFriendRequestNotification(fromUserId: string) {
    const incoming = await fetchIncomingFriendRequests();
    const match = incoming.find((r: any) => String(r.fromUserId) === String(fromUserId));
    if (!match?.id) return;
    const title = `${match.fromName || match.fromUsername || 'Someone'} sent you a friend request`;
    addNotification({
      id: `friendreq:${match.id}`,
      type: 'friend_request',
      title,
      body: '',
      createdAt: new Date(match.createdAt || Date.now()).getTime(),
      unread: true,
      userId: match.fromUserId,
      avatarUrl: match.fromAvatarUrl || null,
      requestId: match.id,
    });
  }
  async function respondFriendRequest(requestId: string, action: 'accept' | 'decline', notifId: string) {
    try {
      await api.postAuth('/friends/respond', { requestId, action }, token);
      removeNotification(notifId);
      try { socket.emit('friends:list'); } catch {}
    } catch (e: any) {
      // Already accepted/declined elsewhere: the request row is gone (404). Just
      // clear the stale notification instead of surfacing an error.
      if (e?.status === 404) { removeNotification(notifId); return; }
      toast(e?.message || 'Failed to respond to friend request', 'error');
    }
  }
  function openSpaceRequests(spaceId: string) {
    try { localStorage.setItem('__settings_target_space__', spaceId); } catch {}
    setNotificationsOpen(false);
    onOpenSettings();
  }
  function openUserProfile(userId: string) {
    setNotificationsOpen(false);
    handleOpenProfile(userId);
  }
  async function actOnJoinRequest(spaceId: string, requestId: string, action: 'approve' | 'reject', notifId: string) {
    try {
      await api.postAuth(`/spaces/${encodeURIComponent(spaceId)}/join-requests/${encodeURIComponent(requestId)}/${action}`, {}, token);
      removeNotification(notifId);
      try { socket.emit('void:list'); } catch {}
    } catch (e: any) {
      toast(e?.message || 'Failed to update request', 'error');
    }
  }
  // The item callbacks are rebuilt only when the list/token change, but must call the newest parent callbacks.
  const actionsRef = useLatest({ actOnJoinRequest, openSpaceRequests, openUserProfile, respondFriendRequest });
  const notificationItems = useMemo(() => {
    return notifications.map((n) => {
      if (n.type === 'space_request' && n.spaceId && n.requestId) {
        return {
          ...n,
          onOpen: () => actionsRef.current.openSpaceRequests(n.spaceId as string),
          actions: [
            { label: 'Approve', style: 'primary' as const, onClick: () => actionsRef.current.actOnJoinRequest(n.spaceId as string, n.requestId as string, 'approve', n.id) },
            { label: 'Reject', style: 'danger' as const, onClick: () => actionsRef.current.actOnJoinRequest(n.spaceId as string, n.requestId as string, 'reject', n.id) },
          ],
        };
      }
      if (n.type === 'friend_request' && n.requestId) {
        return {
          ...n,
          onOpen: () => n.userId && actionsRef.current.openUserProfile(n.userId),
          actions: [
            { label: 'Accept', style: 'primary' as const, onClick: () => actionsRef.current.respondFriendRequest(n.requestId as string, 'accept', n.id) },
            { label: 'Decline', style: 'danger' as const, onClick: () => actionsRef.current.respondFriendRequest(n.requestId as string, 'decline', n.id) },
          ],
        };
      }
      if (n.type === 'friend_request' && n.userId) {
        return { ...n, onOpen: () => actionsRef.current.openUserProfile(n.userId as string) };
      }
      if ((n.type === 'profile_photo' || n.type === 'activity') && n.userId) {
        return { ...n, onOpen: () => actionsRef.current.openUserProfile(n.userId as string) };
      }
      return n;
    });
  }, [notifications, actionsRef]);
  const [friendReqCount, setFriendReqCount] = useState<number>(0);
  // Clear unseen badge when opening the Friends modal
  useEffect(() => { if (friendsOpen) setFriendReqCount(0); }, [friendsOpen]);
  // Dedup set for incoming notification events (by messageId)
  const notifySeenIdsRef = useRef<Set<string>>(new Set());

  // Registered once per socket/token, so reach the parent callbacks through a ref to always call the newest.
  const fnsRef = useLatest({ addFriendRequestNotification, getUserSummary, refreshFriends });
  useEffect(() => {
    if (!socket) return;

    const onJoinRequestsNew = (payload: any) => {
      const spaceId = String(payload?.spaceId || '');
      const requestId = String(payload?.requestId || '');
      if (!spaceId || !requestId) return;
      const title = `${payload?.name || payload?.username || 'Someone'} requested access`;
      const body = payload?.spaceName ? `Space: ${payload.spaceName}` : `Space: ${spaceId}`;
      addNotification({
        id: `joinreq:${requestId}`,
        type: 'space_request',
        title,
        body,
        createdAt: Date.now(),
        unread: true,
        userId: payload?.userId,
        spaceId,
        requestId,
        avatarUrl: payload?.avatarUrl || null,
      });
    };
    const onJoinRequestsResolved = (payload: any) => {
      const requestId = String(payload?.requestId || '');
      if (!requestId) return;
      removeNotification(`joinreq:${requestId}`);
    };
    const onUserUpdate = async (payload: any) => {
      const type = String(payload?.type || '');
      const userId = String(payload?.userId || '');
      if (!userId || !type) return;
      const name = payload?.name || payload?.username || 'Someone';
      const avatarUrl = payload?.avatarUrl || null;
      if (type === 'avatar') {
        addNotification({
          id: `avatar:${userId}:${payload?.updatedAt || Date.now()}`,
          type: 'profile_photo',
          title: `${name} updated their profile photo`,
          body: '',
          createdAt: Date.now(),
          unread: true,
          userId,
          avatarUrl,
        });
      } else if (type === 'activity') {
        const activity = String(payload?.activity || '').trim();
        addNotification({
          id: `activity:${userId}:${payload?.updatedAt || Date.now()}`,
          type: 'activity',
          title: `${name} updated their activity`,
          body: activity ? `“${activity}”` : '',
          createdAt: Date.now(),
          unread: true,
          userId,
          avatarUrl,
        });
      }
    };
    const onFriendsUpdate = async (payload: any) => {
      const type = String(payload?.type || '');
      if (type === 'request' && payload?.fromUserId) {
        await fnsRef.current.addFriendRequestNotification(String(payload.fromUserId));
        return;
      }
      if (type === 'request-resolved' && payload?.requestId) {
        // The incoming request was accepted/declined (here or on another device) —
        // drop its notification so it can't be acted on again.
        removeNotification(`friendreq:${String(payload.requestId)}`);
        fnsRef.current.refreshFriends();
        return;
      }
      if (type === 'accepted' && payload?.userId) {
        const u = await fnsRef.current.getUserSummary(String(payload.userId));
        if (!u) return;
        addNotification({
          id: `friendaccept:${u.id}:${Date.now()}`,
          type: 'friend_request',
          title: `${u.name || u.username || 'Someone'} accepted your friend request`,
          body: '',
          createdAt: Date.now(),
          unread: true,
          userId: u.id,
          avatarUrl: u.avatarUrl || null,
        });
        return;
      }
      fnsRef.current.refreshFriends();
    };

    socket.on("join-requests:new", onJoinRequestsNew);
    socket.on("join-requests:resolved", onJoinRequestsResolved);
    socket.on("user:update", onUserUpdate);
    socket.on("friends:update", onFriendsUpdate);
    return () => {
      socket.off("join-requests:new", onJoinRequestsNew);
      socket.off("join-requests:resolved", onJoinRequestsResolved);
      socket.off("user:update", onUserUpdate);
      socket.off("friends:update", onFriendsUpdate);
    };
  }, [socket, token, fnsRef]);

  return {
    notificationsOpen,
    setNotificationsOpen,
    unreadNotificationCount,
    notificationItems,
    markAllRead,
    removeNotification,
    friendReqCount,
    setFriendReqCount,
    notifySeenIdsRef,
  };
}


